import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../context/AlertContext';
import './SRTLAMonitor.css';

function SRTLAMonitor() {
  const { socket } = useSocket();
  const alert = useAlert();
  const [servers, setServers] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddServer, setShowAddServer] = useState(false);
  const [newServer, setNewServer] = useState({ name: '', url: '' });

  // OBS Sync State
  const [syncConnected, setSyncConnected] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [referenceSource, setReferenceSource] = useState('');
  const [targetSources, setTargetSources] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [syncOffsets, setSyncOffsets] = useState({});
  const [manualSource, setManualSource] = useState('');
  const [manualOffset, setManualOffset] = useState(0);

  useEffect(() => {
    loadServers();
    loadStreams();
    loadSyncStatus();
    loadOBSSources();

    if (socket) {
      socket.on('srtla:stats', handleStatsUpdate);
      socket.on('srtla:stream:connected', handleStreamConnected);
      socket.on('srtla:stream:disconnected', handleStreamDisconnected);
      socket.on('srtla:alert', handleAlert);
      socket.on('srtla:error', handleError);
      
      // OBS Sync Events
      socket.on('sync:status', handleSyncStatus);
      socket.on('sync:update', handleSyncUpdate);
      socket.on('sync:agentDisconnected', handleAgentDisconnected);

      return () => {
        socket.off('srtla:stats', handleStatsUpdate);
        socket.off('srtla:stream:connected', handleStreamConnected);
        socket.off('srtla:stream:disconnected', handleStreamDisconnected);
        socket.off('srtla:alert', handleAlert);
        socket.off('srtla:error', handleError);
        socket.off('sync:status', handleSyncStatus);
        socket.off('sync:update', handleSyncUpdate);
        socket.off('sync:agentDisconnected', handleAgentDisconnected);
      };
    }
  }, [socket]);

  const loadServers = async () => {
    try {
      const { data } = await axios.get('/api/srtla/servers');
      setServers(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading servers:', error);
      setLoading(false);
    }
  };

  const loadStreams = async () => {
    try {
      const { data } = await axios.get('/api/srtla/streams');
      setStreams(data);
    } catch (error) {
      console.error('Error loading streams:', error);
    }
  };

  const handleStatsUpdate = (stats) => {
    setStreams(stats.streams || []);
  };

  const handleStreamConnected = (stream) => {
    console.log('Stream connected:', stream);
    loadStreams();
  };

  const handleStreamDisconnected = (stream) => {
    console.log('Stream disconnected:', stream);
    loadStreams();
  };

  const handleAlert = (alert) => {
    console.log('SRTLA Alert:', alert);
    // Could show notification here
  };

  const handleError = (error) => {
    console.error('SRTLA Error:', error);
  };

  // OBS Sync Functions
  const loadSyncStatus = async () => {
    try {
      const { data } = await axios.get('/api/obs-sync/status');
      setSyncConnected(data.connected);
      if (data.status) {
        setSyncEnabled(data.status.enabled);
        setReferenceSource(data.status.reference || '');
        setTargetSources(data.status.targets || []);
        setSyncOffsets(data.status.offsets || {});
      }
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const loadOBSSources = async () => {
    // Load available sources from streams
    const sourceNames = publishers.map(s => s.name);
    setAvailableSources(sourceNames.length > 0 ? sourceNames : ['Handy-Cam', 'Feste-Cam', 'Cam3']);
  };

  const handleSyncStatus = (data) => {
    setSyncConnected(true);
    setSyncEnabled(data.enabled);
    setReferenceSource(data.reference || '');
    setTargetSources(data.targets || []);
    setSyncOffsets(data.offsets || {});
  };

  const handleSyncUpdate = (data) => {
    setSyncOffsets(prev => ({
      ...prev,
      [data.source]: data.offset
    }));
    
    if (data.manual) {
      alert.success(`Manual offset ${data.offset}ms applied to ${data.source}`);
    } else {
      alert.info(`Auto-sync: ${data.delay}ms delay detected on ${data.source}`);
    }
  };

  const handleAgentDisconnected = () => {
    setSyncConnected(false);
    setSyncEnabled(false);
    alert.warning('Sync Agent disconnected');
  };

  const handleStartSync = async () => {
    if (!referenceSource || targetSources.length === 0) {
      alert.error('Please select reference and target sources');
      return;
    }

    try {
      await axios.post('/api/obs-sync/start', {
        reference: referenceSource,
        targets: targetSources
      });
      alert.success('Sync started');
    } catch (error) {
      alert.error('Failed to start sync: ' + error.message);
    }
  };

  const handleStopSync = async () => {
    try {
      await axios.post('/api/obs-sync/stop');
      alert.success('Sync stopped');
    } catch (error) {
      alert.error('Failed to stop sync: ' + error.message);
    }
  };

  const handleManualSync = async () => {
    if (!manualSource) {
      alert.error('Please select a source');
      return;
    }

    try {
      await axios.post('/api/obs-sync/manual', {
        source: manualSource,
        offset: parseInt(manualOffset)
      });
    } catch (error) {
      alert.error('Failed to apply manual sync: ' + error.message);
    }
  };

  const toggleTargetSource = (source) => {
    setTargetSources(prev => {
      if (prev.includes(source)) {
        return prev.filter(s => s !== source);
      } else {
        return [...prev, source];
      }
    });
  };

  const addServer = async () => {
    try {
      await axios.post('/api/srtla/servers', newServer);
      setNewServer({ name: '', url: '' });
      setShowAddServer(false);
      loadServers();
    } catch (error) {
      console.error('Error adding server:', error);
    }
  };

  const deleteServer = async (id) => {
    if (!window.confirm('Server wirklich löschen?')) return;
    
    try {
      await axios.delete(`/api/srtla/servers/${id}`);
      loadServers();
    } catch (error) {
      console.error('Error deleting server:', error);
    }
  };

  const toggleServer = async (server) => {
    try {
      await axios.put(`/api/srtla/servers/${server.id}`, {
        ...server,
        enabled: server.enabled ? 0 : 1
      });
      loadServers();
    } catch (error) {
      console.error('Error toggling server:', error);
    }
  };

  const getStabilityColor = (stability) => {
    if (stability >= 80) return '#4caf50';
    if (stability >= 50) return '#ff9800';
    return '#f44336';
  };

  const getStabilityLabel = (stability) => {
    if (stability >= 80) return 'Ausgezeichnet';
    if (stability >= 50) return 'Mittel';
    return 'Schlecht';
  };

  const formatBitrate = (mbps) => {
    return `${mbps.toFixed(2)} Mbps`;
  };

  const formatUptime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const publishers = streams.filter(s => s.type === 'publisher');
  const subscribers = streams.filter(s => s.type === 'subscriber');

  if (loading) {
    return <div className="srtla-monitor"><div className="loading">Lade...</div></div>;
  }

  return (
    <div className="srtla-monitor">
      <div className="page-header">
        <h2>📡 SRTLA Monitor</h2>
        <button className="btn-add" onClick={() => setShowAddServer(true)}>
          + Server hinzufügen
        </button>
      </div>

      {showAddServer && (
        <div className="add-server-modal">
          <div className="modal-content">
            <h3>Neuer SRTLA Server</h3>
            <input
              type="text"
              placeholder="Name (z.B. Main Server)"
              value={newServer.name}
              onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="URL (z.B. http://85.214.38.219:8181)"
              value={newServer.url}
              onChange={(e) => setNewServer({ ...newServer, url: e.target.value })}
            />
            <div className="modal-actions">
              <button onClick={addServer}>Hinzufügen</button>
              <button onClick={() => setShowAddServer(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      <div className="servers-list">
        {servers.map(server => (
          <div key={server.id} className="server-card">
            <div className="server-header">
              <h3>{server.name}</h3>
              <div className="server-actions">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={server.enabled === 1}
                    onChange={() => toggleServer(server)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <button className="btn-delete" onClick={() => deleteServer(server.id)}>
                  🗑️
                </button>
              </div>
            </div>
            <div className="server-url">{server.url}</div>
          </div>
        ))}
      </div>

      <div className="streams-section">
        <h3>📥 Eingehende Streams ({publishers.length})</h3>
        {publishers.length === 0 ? (
          <div className="no-streams">Keine aktiven Publisher</div>
        ) : (
          <div className="streams-grid">
            {publishers.map(stream => (
              <div key={stream.name} className="stream-card">
                <div className="stream-header">
                  <div className="stream-name">{stream.name}</div>
                  <div 
                    className="stream-status"
                    style={{ backgroundColor: getStabilityColor(stream.stability) }}
                  >
                    ● Online
                  </div>
                </div>

                <div className="stream-stats">
                  <div className="stat">
                    <span className="stat-label">Bitrate</span>
                    <span className="stat-value">{formatBitrate(stream.mbpsRecvRate)}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Bandbreite</span>
                    <span className="stat-value">{formatBitrate(stream.mbpsBandwidth)}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">RTT</span>
                    <span className="stat-value">{stream.rtt.toFixed(0)} ms</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Paketverlust</span>
                    <span className="stat-value">{stream.pktRcvLoss}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Buffer</span>
                    <span className="stat-value">{stream.msRcvBuf} ms</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Uptime</span>
                    <span className="stat-value">{formatUptime(stream.uptime)}</span>
                  </div>
                </div>

                <div className="stability-bar">
                  <div className="stability-label">
                    Stabilität: {getStabilityLabel(stream.stability)} ({stream.stability}%)
                  </div>
                  <div className="stability-progress">
                    <div 
                      className="stability-fill"
                      style={{ 
                        width: `${stream.stability}%`,
                        backgroundColor: getStabilityColor(stream.stability)
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="streams-section">
        <h3>📤 Ausgehende Streams ({subscribers.length})</h3>
        {subscribers.length === 0 ? (
          <div className="no-streams">Keine aktiven Subscriber</div>
        ) : (
          <div className="streams-grid">
            {subscribers.map(stream => (
              <div key={stream.name} className="stream-card">
                <div className="stream-header">
                  <div className="stream-name">{stream.name}</div>
                  <div className="stream-status online">● Online</div>
                </div>
                <div className="stream-stats">
                  <div className="stat">
                    <span className="stat-label">Bitrate</span>
                    <span className="stat-value">{formatBitrate(stream.mbpsRecvRate)}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Uptime</span>
                    <span className="stat-value">{formatUptime(stream.uptime)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* OBS Sync Section */}
      <div className="obs-sync-section">
        <div className="section-header">
          <h3>🔄 OBS Stream Synchronisation</h3>
          <div className="sync-status-badges">
            {syncConnected ? (
              <span className="status-badge connected">✅ Agent verbunden</span>
            ) : (
              <span className="status-badge disconnected">❌ Agent nicht verbunden</span>
            )}
            {syncEnabled && (
              <span className="status-badge syncing">🔄 Sync aktiv</span>
            )}
          </div>
        </div>

        {!syncConnected && (
          <div className="sync-warning">
            <p>⚠️ Der OBS Sync Agent muss auf dem Streaming-Server laufen.</p>
            <p>Siehe <code>obs-sync-agent/README.md</code> für Installation.</p>
          </div>
        )}

        <div className="sync-controls-grid">
          {/* Auto Sync */}
          <div className="sync-control-card">
            <h4>⚙️ Automatische Synchronisation</h4>
            
            <div className="form-group-compact">
              <label>Referenz-Quelle</label>
              <select 
                value={referenceSource} 
                onChange={(e) => setReferenceSource(e.target.value)}
                disabled={syncEnabled}
              >
                <option value="">Wähle Referenz...</option>
                {availableSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            <div className="form-group-compact">
              <label>Zu synchronisierende Quellen</label>
              <div className="source-checkboxes-compact">
                {availableSources
                  .filter(s => s !== referenceSource)
                  .map(source => (
                    <label key={source} className="checkbox-label-compact">
                      <input
                        type="checkbox"
                        checked={targetSources.includes(source)}
                        onChange={() => toggleTargetSource(source)}
                        disabled={syncEnabled}
                      />
                      <span>{source}</span>
                    </label>
                  ))}
              </div>
            </div>

            <div className="sync-actions">
              {!syncEnabled ? (
                <button 
                  className="btn-sync-start"
                  onClick={handleStartSync}
                  disabled={!syncConnected || !referenceSource || targetSources.length === 0}
                >
                  ▶️ Sync starten
                </button>
              ) : (
                <button 
                  className="btn-sync-stop"
                  onClick={handleStopSync}
                >
                  ⏹️ Sync stoppen
                </button>
              )}
            </div>
          </div>

          {/* Manual Sync */}
          <div className="sync-control-card">
            <h4>🎛️ Manueller Offset</h4>
            
            <div className="form-group-compact">
              <label>Quelle</label>
              <select 
                value={manualSource} 
                onChange={(e) => setManualSource(e.target.value)}
              >
                <option value="">Wähle Quelle...</option>
                {availableSources.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>

            <div className="form-group-compact">
              <label>Offset (ms)</label>
              <input
                type="number"
                value={manualOffset}
                onChange={(e) => setManualOffset(e.target.value)}
                step="100"
                placeholder="z.B. 500"
              />
              <small>+ = verzögern | - = beschleunigen</small>
            </div>

            <button 
              className="btn-sync-apply"
              onClick={handleManualSync}
              disabled={!syncConnected || !manualSource}
            >
              ✓ Anwenden
            </button>
          </div>

          {/* Current Offsets */}
          <div className="sync-control-card full-width">
            <h4>📊 Aktuelle Offsets</h4>
            
            {Object.keys(syncOffsets).length === 0 ? (
              <p className="no-data-compact">Keine Offsets aktiv</p>
            ) : (
              <div className="offsets-grid-compact">
                {Object.entries(syncOffsets).map(([source, offset]) => (
                  <div key={source} className="offset-item-compact">
                    <div className="offset-source-compact">{source}</div>
                    <div className={`offset-value-compact ${offset > 0 ? 'positive' : offset < 0 ? 'negative' : 'zero'}`}>
                      {offset > 0 ? '+' : ''}{offset}ms
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SRTLAMonitor;
